import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const MAX_VISIBLE_MS = 15000; // safety fallback for extended flows
const SHORT_VISIBLE_MS = 600; // quick auto-hide for simple pages
const FADE_MS = 250;

const RouteOverlaySpinner = () => {
  const location = useLocation();
  const [mounted, setMounted] = useState(false);
  const [opaque, setOpaque] = useState(false);
  const hideTimerRef = useRef(null);
  const fadeTimerRef = useRef(null);
  const extendedRef = useRef(false); // set to true when app:nav-start is received

  const clearTimers = () => {
    if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
    if (fadeTimerRef.current) { clearTimeout(fadeTimerRef.current); fadeTimerRef.current = null; }
  };

  const showOverlay = () => {
    clearTimers();
    setMounted(true);
    requestAnimationFrame(() => setOpaque(true));
  };

  const hideOverlay = () => {
    clearTimers();
    setOpaque(false);
    fadeTimerRef.current = setTimeout(() => setMounted(false), FADE_MS);
  };

  useEffect(() => {
    // On route change, show overlay with a short auto-hide unless extended by app:nav-start
    extendedRef.current = false;
    showOverlay();
    clearTimers();
    hideTimerRef.current = setTimeout(() => {
      if (!extendedRef.current) hideOverlay();
    }, SHORT_VISIBLE_MS);

    return () => { clearTimers(); };
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const onNavStart = () => {
      extendedRef.current = true;
      showOverlay();
      clearTimers();
      // extended flows use long fallback; real hide happens on app:nav-end
      hideTimerRef.current = setTimeout(() => hideOverlay(), MAX_VISIBLE_MS);
    };
    const onNavEnd = () => {
      extendedRef.current = false;
      hideOverlay();
    };
    window.addEventListener('app:nav-start', onNavStart);
    window.addEventListener('app:nav-end', onNavEnd);
    return () => {
      window.removeEventListener('app:nav-start', onNavStart);
      window.removeEventListener('app:nav-end', onNavEnd);
      clearTimers();
    };
  }, []);

  if (!mounted) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(255,255,255,0.7)",
        zIndex: 9999,
        opacity: opaque ? 1 : 0,
        transition: `opacity ${FADE_MS}ms ease`,
      }}
      aria-label="Loading"
    >
      <div className="spinner-border text-primary" role="status" />
    </div>
  );
};

export default RouteOverlaySpinner;


